 
import { Button, Input, Loader } from 'ccs-ui-components';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AUTH_METHOD, AUTH_TYPE, AUTH_TYPE_NAME, CREATE_SERVICE_NAME_INVALID_ERROR, FORM_SERVICE_TYPE, SERVICE_DETAILS_MAXLENGTH, SERVICE_TYPE, SERVICE_TYPE_KEY, SERVICE_TYPE_KEY_VALUE, URL_PATH } from '../../../../constants';
import { useServiceDetailForm, useServiceDetailsFormLaunch } from '../../../../hooks';
import { INameSpaceParam, IQueryPathParam, IServiceDetailsValidationModel } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { serviceDetailsIncomplete } from '../../../../store/reducer/services/NavBarReducer';
import { checkProvideAuthenticationClicked, updateImportStatus } from '../../../../store/reducer/services/ServiceDetailsReducer';
import { setIsServiceNameError } from '../../../../store/reducer/services/ServiceOperationMonitorReducer';
import { objToArrForQueryAndHeader } from '../../../../utilities/common/ObjectToArrayUtils';
import { addServiceDetailsToStore, getConvertedHeader, getConvertedQueryParam, getConvertedRequestParam, isServiceTypeNotExternal, setUsernamePassWordEmpty, updateServiceOperationsOnServiceTypeChange } from '../../../../utilities/services/ServiceDetailsUtils';
import AuthBlockFields from './auth-block/AuthBlock';
import CheckBox from './auth-check/CheckBox';
import ServiceDescription from './service-description/ServiceDescription';
import ViewServiceDetailsButtons from './service-details-button/ViewServiceDetailsButtons';
import ServiceDetailsHeader from './service-details-header/ServiceDetailsHeader';
import ServiceEndpoint from './service-endpoint/ServiceEndPoint';
import ServiceType from './service-type/ServiceType';
import SoapParamFields from './soap-param-field/SoapParamFields';

import classes from './ServiceDetailsForm.module.scss';

interface IServiceDetailsFormProps {
  isEdit: boolean;
  viewMode: boolean;
  showServiceNameExistWarning:(showServiceExistWarning: boolean)=> void;
}

interface IServiceDetailsProps {
  isEdit: boolean;
  viewMode: boolean;
  showServiceNameExistWarning:(showServiceExistWarning: boolean)=> void;
  ref?: React.ForwardedRef<ServiceDetailsFormRef>;
}

export interface ServiceDetailsFormRef {
  onHighlightFieldClick: () => void;
}

const ServiceDetails = ({ isEdit, viewMode, showServiceNameExistWarning, ref = null }: IServiceDetailsProps) => {
  const isCreateMode = !viewMode && !isEdit;
  const inputRef = useRef<HTMLInputElement>(null);
  /* Local state */
  const {
    serviceName: reducerServiceName,
    endPoint: reducerEndPoint,
    serviceType: reducerServiceType,
    serviceDescription: reducerServiceDescription,
    authName: reducerAuthName,
    checkClicked: reducerCheckClicked,
    authDetails: reducerAuthDetails,
    soapAttributes: reducerSoapAttributes,
    importStatus
  } = useSelector((state) => state.serviceDetailsReducer);
  const [serviceTypeValue, setServiceTypeValue] = useState<string | undefined>(
    ''
  );
  const [authTypeValue, setAuthTypeValue] = useState<string | undefined>('');
  const [authMethodValue, setAuthMethodValue] = useState<string | undefined>(
    ''
  );
  const [headerParamList, setHeaderParamList] = useState<IQueryPathParam[]>(
    objToArrForQueryAndHeader(reducerAuthDetails?.headerParam)
  );
  const [queryPathParamList, setQueryPathParamList] = useState<
    IQueryPathParam[]
  >(objToArrForQueryAndHeader(reducerAuthDetails?.queryPathParam));
  const [requestParamList, setRequestParamList] = useState<IQueryPathParam[]>(
    objToArrForQueryAndHeader(reducerAuthDetails?.requestParam)
  );
  const [soapNamespaceList, setSoapNamespaceList] = useState<
    INameSpaceParam[] | undefined
  >([]);
  const [changeTypeImportStatus, setChangeTypeImportStatus] = useState<boolean>(false);
  /* Hooks */
  const { handleInputChange, formData, setFormParamValues, recheckValidation } = useServiceDetailForm();
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  const cloneServiceName = location.state?.cloneServiceName ?? '';
  const {
    apiResponseStatus,
    serviceNameValidation,
    validateFormDataForUI,
    requestParamListHandler,
    queryPathParamListHandler,
    setParamsListEmpty,
    soapNamespaceListHandler,
    validateXml,
    headerParamListHandler
  } = useServiceDetailsFormLaunch(
    [headerParamList, setHeaderParamList],
    [queryPathParamList, setQueryPathParamList],
    [requestParamList, setRequestParamList],
    [soapNamespaceList, setSoapNamespaceList],
    [formData, setFormParamValues],
    authTypeValue
  );
  /* Get Data from the store */
  const { serviceOperations } = useSelector(
    (state) => state.serviceOperationReducer
  );
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );
  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );
  const { serviceDetails: navBarServiceDetails } = useSelector(
    (state) => state.navBarStateReducer
  );
  const { serviceDetails, isLoading, serviceErrorMessage } = useSelector(
    (state) => state.getServiceNameReducer
  );
  const { isServiceNameError, showServiceExistWarning } = useSelector(
    (state) => state.serviceOperationMonitorReducer
  );
  const activeService = serviceDetails[0];

  const HELPER_TEXT = {
    service_name_ht: translate('helper_text.txt_input_search_name'),
  };

  const SERVICE_VALIDATION_ERRORS = {
    serviceCheckError: translate('service_validation_error.serviceCheckError')
  };

  const onHighlightFieldClick = () => {
    inputRef?.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    onHighlightFieldClick
  }));

  /**
   * Reset the auth method
   * @param formData service details form data
   */
  const resetAuthMethod = (formData: IServiceDetailsValidationModel) => {
    setAuthMethodValue('');
    formData.authMethod.value = '';
  };

  /**
   * Reset the auth type
   * @param formData service details form data
   */
  const resetAuthType = (formData: IServiceDetailsValidationModel) => {
    setAuthTypeValue('');
    formData.authType.value = '';
  };

  // Service Type change handler
  const serviceTypeChangeHandler = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const index = parseInt(e.target.value, 10);
    setServiceTypeValue(e.target.value);
    const newFormData = { ...formData };
    newFormData.serviceType.value = SERVICE_TYPE[index]?.name;
    newFormData.headerXml.value = '';
    newFormData.headerXml.isValid = true;
    if (!changeTypeImportStatus) {
      newFormData.endpoint.value = '';
      newFormData.endpoint.isTouched = false;
      newFormData.endpoint.isValid = true;
      updateServiceOperationsOnServiceTypeChange(
        serviceOperations,
        ingressEventMappingList,
        egressEventMappingList,
        dispatch,
        reducerServiceType,
        SERVICE_TYPE[index]?.name
      );
    }
    if (SERVICE_TYPE[index]?.name === SERVICE_TYPE_KEY.DB2) {
      setAuthTypeValue('0');
      newFormData.authType.value = AUTH_TYPE_NAME.BASIC;
      dispatch(checkProvideAuthenticationClicked(true));
    } else {
      resetAuthType(newFormData);
      dispatch(checkProvideAuthenticationClicked(false));
    }
    // on service type change, reset all service details
    dispatch(serviceDetailsIncomplete());
    addServiceDetailsToStore(
      newFormData,
      dispatch,
      index.toString() === SERVICE_TYPE_KEY_VALUE.DB2,
      [],
      [],
      [],
      []
    );
    resetAuthMethod(newFormData);
    setUsernamePassWordEmpty(newFormData);
    setParamsListEmpty();
    setSoapNamespaceList([]);
    setFormParamValues(newFormData);
  };

  /**
   * Calls on Auth type changes
   * @param event: event
   */
  const authTypeChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const index = parseInt(event.target.value, 10);
    setAuthTypeValue(event.target.value);
    const newFormData = { ...formData };
    newFormData.authType.value = AUTH_TYPE[index]?.name;
    resetAuthMethod(newFormData);
    setUsernamePassWordEmpty(newFormData);
    setParamsListEmpty();
    setFormParamValues(newFormData);
    addServiceDetailsToStore(
      newFormData,
      dispatch,
      true,
      headerParamList,
      requestParamList,
      queryPathParamList,
      soapNamespaceList
    );
  };

  /**
   * Calls on auth method change
   * @param e : change event
   */
  const authMethodChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.target.value, 10);
    setAuthMethodValue(e.target.value);
    const newFormData = { ...formData };
    newFormData.authMethod.value = AUTH_METHOD[index]?.name;
    setFormParamValues(newFormData);
  };

  // Sets state to true when Provide authentication details button is clicked
  const handleCheckBoxChange = () => {
    dispatch(checkProvideAuthenticationClicked(!reducerCheckClicked));
    dispatch(serviceDetailsIncomplete());
    const newFormData = { ...formData };
    resetAuthMethod(newFormData);
    resetAuthType(newFormData);
    setUsernamePassWordEmpty(newFormData);
    setParamsListEmpty();
    setFormParamValues(newFormData);

    addServiceDetailsToStore(
      newFormData,
      dispatch,
      !reducerCheckClicked,
      headerParamList,
      requestParamList,
      queryPathParamList,
      soapNamespaceList
    );
  };

  /**
   * Add data to store on continue button click
   */
  const continueClickHandler = () => {
    addServiceDetailsToStore(
      formData,
      dispatch,
      reducerCheckClicked,
      headerParamList,
      requestParamList,
      queryPathParamList,
      soapNamespaceList
    );
    // need to concatenate 2 string constants due router V6 behavior
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_EXCEPTION}`, { state: location.state });
  };

  const getServiceType = (reducerServiceType: string) => {
    if (!reducerServiceType) {
      const serviceIndex = parseInt(String(serviceTypeValue), 10);
      setServiceTypeValue(String(serviceIndex));
      return SERVICE_TYPE[serviceIndex]?.name;
    }
    setServiceTypeValue(
      `${SERVICE_TYPE.findIndex((item) => item.name === reducerServiceType)}`
    );
    return reducerServiceType;
  };

  const getAuthMethod = (reducerAuthMethod: string) => {
    if (!reducerAuthMethod?.length) {
      setAuthMethodValue(reducerAuthMethod);
      return authMethodValue
        ? AUTH_METHOD[parseInt(authMethodValue, 10)]?.name
        : '';
    }
    setAuthMethodValue(
      `${AUTH_METHOD.findIndex((item) => item.name === reducerAuthMethod)}`
    );
    return reducerAuthMethod;
  };

  const getAuthType = (reducerAuthType: string) => {
    if (!reducerAuthType?.length) {
      setAuthTypeValue(reducerAuthType);
      return authTypeValue ? AUTH_TYPE[parseInt(authTypeValue, 10)]?.name : '';
    }
    setAuthTypeValue(
      `${AUTH_TYPE.findIndex((item) => item.name === reducerAuthType)}`
    );
    return reducerAuthType;
  };

  const setParamsFromStore = () => {
    setHeaderParamList(
      objToArrForQueryAndHeader(reducerAuthDetails?.headerParam) ?? []
    );
    setQueryPathParamList(
      objToArrForQueryAndHeader(reducerAuthDetails?.queryPathParam) ?? []
    );
    setRequestParamList(
      objToArrForQueryAndHeader(reducerAuthDetails?.requestParam) ?? []
    );
    setSoapNamespaceList(reducerSoapAttributes?.envelopeNamespaces ?? []);
  };

  useEffect(() => {
    if (importStatus) {
      dispatch(updateImportStatus(false));
      setChangeTypeImportStatus(true);
      setFormParamValues((preValue: IServiceDetailsValidationModel) => {
        const newFormData = { ...preValue };
        newFormData.serviceName.value = reducerServiceName;
        newFormData.serviceName.isValid = true;
        newFormData.endpoint.value = reducerEndPoint;
        newFormData.serviceType.value = '';
        newFormData.serviceDescription.value = reducerServiceDescription;
        setServiceTypeValue('');
        const recheckArray: string[] = [];
        if (reducerServiceName) {
          serviceNameValidation();
        }
        recheckArray.push('serviceName');
        if (reducerEndPoint) recheckArray.push('endpoint');
        recheckArray.push('serviceDescription');
        recheckValidation(recheckArray);
        return newFormData;
      });
    }
  }, [importStatus]);

  useEffect(() => {
    if (isServiceNameError && !viewMode) {
      const newFormData = { ...formData };
      newFormData.serviceName.error = SERVICE_VALIDATION_ERRORS.serviceCheckError;
      newFormData.serviceName.isValid = false;
      newFormData.serviceName.isTouched = true;
      setFormParamValues(newFormData);
      dispatch(setIsServiceNameError(false));
    }
  }, [isServiceNameError]);

  useEffect(() => {
    validateFormDataForUI();
  }, [
    formData,
    reducerCheckClicked,
    headerParamList,
    requestParamList,
    soapNamespaceList,
    queryPathParamList
  ]);

  useEffect(() => {
    // set the error message if service exist
    if (serviceDetails.length && serviceDetails[0]?.serviceName && !cloneServiceName.length) {
      const newFormData = { ...formData };
      newFormData.serviceName.isValid = false;
      newFormData.serviceName.error = serviceErrorMessage;
      setFormParamValues(newFormData);
    } else if (
      isCreateMode
      && !cloneServiceName.length
      && !formData.serviceName.isValid
      && formData.serviceName.error === CREATE_SERVICE_NAME_INVALID_ERROR
    ) {
      // Re-entering the Create Service flow (e.g., from Journey Designer after a previous
      // successful create) leaves stale serviceDetails in Redux for one render. The branch
      // above writes the "exists" error into formData before the parent cleanup effect runs
      // and empties serviceDetails. Reset the stale error once the store has been cleared so
      // the user does not see "Service name already exists" on a fresh form.
      const newFormData = { ...formData };
      newFormData.serviceName.isValid = true;
      newFormData.serviceName.error = '';
      setFormParamValues(newFormData);
    }
  }, [serviceDetails]);

  useEffect(() => {
    if (!formData.authType.value) {
      setAuthTypeValue('');
    }
  }, [formData.authType.value]);

  const updateAuthData = (formData: IServiceDetailsValidationModel) => {
    if (formData.authType.value === AUTH_TYPE_NAME.BASIC) {
      formData.userName.value = reducerAuthDetails?.username ?? '';
      formData.password.value = reducerAuthDetails?.password ?? '';
    } else if (formData.authType.value === AUTH_TYPE_NAME.OAUTH) {
      formData.authMethod.value = getAuthMethod(
        String(reducerAuthDetails?.authMethod)
      );
      formData.authUrl.value = reducerAuthDetails?.authUrl ?? '';

      const headerArray = getConvertedHeader(
        reducerAuthDetails,
        activeService?.authDetails
      );
      setHeaderParamList(headerArray as IQueryPathParam[]);

      const queryArray = getConvertedQueryParam(
        reducerAuthDetails,
        activeService?.authDetails
      );
      setQueryPathParamList(queryArray as IQueryPathParam[]);

      const requestArray = getConvertedRequestParam(
        reducerAuthDetails,
        activeService?.authDetails
      );
      setRequestParamList(requestArray as IQueryPathParam[]);
    } else if (formData.authType.value === AUTH_TYPE_NAME.APIKEY) {
      const headerArray = getConvertedHeader(
        reducerAuthDetails,
        activeService?.authDetails
      );
      setHeaderParamList(headerArray as IQueryPathParam[]);
    }
  };

  useEffect(() => {
    const newFormData = { ...formData };
    newFormData.serviceName.value = reducerServiceName;
    newFormData.serviceDescription.value = reducerServiceDescription;
    newFormData.endpoint.value = reducerEndPoint;
    newFormData.serviceType.value = getServiceType(reducerServiceType);
    newFormData.authName.value = reducerAuthName ?? '';
    if (reducerAuthDetails?.authType) {
      formData.authType.value = getAuthType(reducerAuthDetails?.authType);
    }
    newFormData.authMethod.value = getAuthMethod(
      reducerAuthDetails?.authMethod ?? ''
    );
    newFormData.authUrl.value = reducerAuthDetails?.authUrl ?? '';
    newFormData.headerXml.value = reducerSoapAttributes?.headersXML ?? '';
    const serviceTypeValue = newFormData.serviceType?.value?.toLowerCase();
    const formServiceTypes = [
      FORM_SERVICE_TYPE.REST,
      FORM_SERVICE_TYPE.DB2,
      FORM_SERVICE_TYPE.MQ
    ];
    setParamsFromStore();
    if (newFormData.serviceType.value === SERVICE_TYPE_KEY.SOAP) {
      newFormData.headerXml.value = reducerSoapAttributes?.headersXML ?? '';
      setSoapNamespaceList(reducerSoapAttributes?.envelopeNamespaces);
    }
    const authIndex = AUTH_TYPE.findIndex(
      (item) => item.name === reducerAuthDetails?.authType
    );
    const authMethIndex = AUTH_METHOD.findIndex(
      (item) => item.name === reducerAuthDetails?.authMethod
    );
    if (authIndex || authIndex === 0) {
      setAuthTypeValue(String(authIndex));
    }

    if (authMethIndex || authMethIndex === 0) {
      setAuthMethodValue(String(authMethIndex));
    }
    if (formServiceTypes.includes(serviceTypeValue) && reducerCheckClicked) {
      updateAuthData(newFormData);
    }
    setFormParamValues(newFormData);
  }, [
    reducerServiceName,
    reducerAuthDetails,
    reducerSoapAttributes,
    reducerCheckClicked
  ]);

  useEffect(() => {
    showServiceNameExistWarning(showServiceExistWarning);
  }, [showServiceExistWarning]);

  return (
    <div
      className={`form-wrapper-container ${classes['service-details-wrapper']}`}
    >
      <Loader show={isLoading} />
      <ServiceDetailsHeader />
      <div className={classes['service-details-body']}>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
          }}
          data-testid="serviceDetailForm"
        >
          <fieldset disabled={viewMode}>
            <Form.Group className="mb-3" controlId="formServiceName">
              <Input
                className={`${showServiceExistWarning ? classes['input-warning'] : ''}`}
                isMandatory
                labelText={String(
                  translate('service_details_form_component.lbl_service_name')
                )}
                infoIconTxt={String(
                  translate(
                    'service_details_form_component.service_name_info_bubble'
                  )
                )}
                type="text"
                name="serviceName"
                data-testid="service-name-input"
                value={formData.serviceName.value}
                onChange={handleInputChange}
                onBlur={serviceNameValidation}
                placeholder={String(
                  translate('service_details_form_component.plchd_service_name')
                )}
                label=""
                helperText={HELPER_TEXT.service_name_ht}
                errorTxt={isCreateMode ? formData.serviceName.error : ''}
                error={isCreateMode ? !formData.serviceName.isValid : false}
                maxLength={Number(
                  SERVICE_DETAILS_MAXLENGTH.serviceNameMaxLength
                )}
                isLoading={apiResponseStatus}
                disabled={apiResponseStatus || isEdit || viewMode}
              />
            </Form.Group>
            <Row>
              <ServiceType
                typeChangeHandler={serviceTypeChangeHandler}
                serviceTypeValue={serviceTypeValue}
              />
              <ServiceEndpoint
                serviceTypeValue={serviceTypeValue}
                formData={formData}
                handleInputChange={handleInputChange}
                isViewMode={viewMode}
              />
            </Row>
            <CheckBox
              serviceTypeValue={serviceTypeValue}
              handleCheckBoxChange={handleCheckBoxChange}
            />
            {reducerCheckClicked && (
              <AuthBlockFields
                formData={formData}
                handleInputChange={handleInputChange}
                authMethodChangeHandler={authMethodChangeHandler}
                authMethodValue={authMethodValue}
                headerParamListHandler={headerParamListHandler}
                headerParamList={headerParamList}
                requestParamListHandler={requestParamListHandler}
                requestParamList={requestParamList}
                queryPathParamListHandler={queryPathParamListHandler}
                queryPathParamList={queryPathParamList}
                serviceTypeValue={serviceTypeValue}
                authTypeChangeHandler={authTypeChangeHandler}
                authTypeValue={authTypeValue}
                isViewMode={viewMode}
              />
            )}
            {serviceTypeValue === SERVICE_TYPE_KEY_VALUE.SOAP && (
              <SoapParamFields
                formData={formData}
                validateXml={validateXml}
                handleInputChange={handleInputChange}
                soapNamespaceList={soapNamespaceList}
                soapNamespaceListHandler={soapNamespaceListHandler}
                isViewMode={viewMode}
              />
            )}
            <ServiceDescription
              handleInputChange={handleInputChange}
              formData={formData}
              isViewMode={viewMode}
              setFormParamValues={setFormParamValues}
            />
          </fieldset>
          {isCreateMode ? (
            <div className={`${classes['btn-container']} ci-button-group`}>
              <Button
                variant="primary"
                className={classes['btn-save']}
                onClick={continueClickHandler}
                disabled={navBarServiceDetails.state !== 0}
                data-testid="service-details-btn"
              >
                {translate(
                  'service_details_form_component.btn_save_and_continue'
                )}
              </Button>
            </div>
          ) : (
            <ViewServiceDetailsButtons
              isEdit={isEdit}
              formData={formData}
              headerParamList={headerParamList}
              requestParamList={requestParamList}
              queryPathParamList={queryPathParamList}
              soapNamespaceList={soapNamespaceList}
            />
          )}
        </Form>
      </div>
    </div>
  );
};
const ServiceDetailsForm = forwardRef<ServiceDetailsFormRef, IServiceDetailsFormProps>(({ isEdit, viewMode, showServiceNameExistWarning }: IServiceDetailsFormProps, ref) => <ServiceDetails isEdit={isEdit} viewMode={viewMode} showServiceNameExistWarning={showServiceNameExistWarning} ref={ref} />);
export default ServiceDetailsForm;
